import {
  getCompanyDataByDomain,
  getCompanyDetailsByCompanyCode,
  getCompanyLoginMethodsByUrl,
} from '@api/company';
import { LoginTypes } from '@api/login';
import { registerUser } from '@api/users';
import LinkText from '@components/Atoms/Link';
import { LoginMethods } from '@enums/LoginMethods';
import { buildCheckYourInboxPath } from '@helpers/auth.helper';
import useIsMobileApp from '@hooks/useIsMobileApp';
import { CompanyLoginMethodTypesInterface, CompanyLoginMethodsInterface } from '@interfaces/index';
import { showToast } from '@shared/atoms';
import { LoadingBackdrop } from '@shared/molecules';
import { ToastMessages } from 'constants/toastMessages';
import { CompanyDetails, castResponse } from 'custom.d';
import React, { Reducer, useReducer, useState } from 'react';
import { useHistory } from 'react-router';
import GetCompanyCodeForm from '../GetCompanyCodeForm';
import RegistrationForm from '../RegistrationForm';
import styles from './index.module.scss';

interface FormInitialState {
  email: string | undefined;
  isAgree: boolean | undefined;
}

enum DispatchTypes {
  'REGISTER_INPUT',
}

interface Action {
  type: DispatchTypes;
  values: { email?: string; isAgree?: boolean };
}

const formInitialState: FormInitialState = {
  email: '',
  isAgree: false,
};

const formReducer: Reducer<FormInitialState, Action> = (state, action) => {
  if (action.type === DispatchTypes.REGISTER_INPUT) {
    return {
      ...state,
      email: action.values.email,
      isAgree: action.values.isAgree,
    };
  }

  return formInitialState;
};

const SignUp: React.FunctionComponent<SignUpProps> = (props) => {
  const { content, handleLoginMethodsToDisplay } = props;
  let { companyURL, organizationId } = props;
  const history = useHistory();
  const isMobileApp = useIsMobileApp();
  const [getCompanyCode, setGetCompanyCode] = useState(false);
  const [codeRetryCount, setCodeRetryCount] = useState(0);
  const [formState, dispatchForm] = useReducer(formReducer, formInitialState);
  const [isLoading, setIsLoading] = useState(false);
  const [companyDomainDetails, setCompanyDomainDetails] = useState<CompanyDetails | null>(null);
  const [loginMethodsToDisplay, setLoginMethodsToDisplay] = useState<
    CompanyLoginMethodTypesInterface[] | []
  >([]);
  const getCompanyDataByEmailDomain = async (email: string) => {
    if (email) {
      const domain = email.split('@')[1];
      const companyDetails = await getCompanyDataByDomain(domain);

      setCompanyDomainDetails(companyDetails);

      return companyDetails;
    }

    setCompanyDomainDetails(null);

    return null;
  };

  const getCompanyDataByCompanyCode = async (companyCode: string) => {
    if (companyCode) {
      const companyDetails = await getCompanyDetailsByCompanyCode(companyCode);

      if (companyDetails) {
        setCompanyDomainDetails(companyDetails);
      }

      return companyDetails;
    }

    setCompanyDomainDetails(null);

    return null;
  };

  const handleCreateUser = async (emailValue: string, isAgreeValue: boolean) => {
    dispatchForm({
      type: DispatchTypes.REGISTER_INPUT,
      values: {
        email: emailValue,
        isAgree: isAgreeValue,
      },
    });

    if (organizationId) {
      createUser(emailValue, isAgreeValue, organizationId);
    } else {
      const companyData = await getCompanyDataByEmailDomain(emailValue);

      if (companyData) {
        companyURL = companyData.company_url;
        organizationId = companyData.id;
        createUser(emailValue, isAgreeValue, organizationId);
      } else {
        setGetCompanyCode(true);
      }
    }
  };

  const handleValidateCompanyCodeCreateUser = async (companyCodeValue: string) => {
    const companyData = await getCompanyDataByCompanyCode(companyCodeValue);

    if (companyData) {
      companyURL = companyData.company_url;
      organizationId = companyData.id;
      if (formState.email && formState.isAgree && organizationId) {
        createUser(formState.email, formState.isAgree, organizationId, companyCodeValue);
      } else {
        dispatchForm({
          type: DispatchTypes.REGISTER_INPUT,
          values: {
            email: '',
            isAgree: false,
          },
        });
        setGetCompanyCode(false);
        showToast(
          ToastMessages.auth.info.PLEASE_ENTER_VALID_DETAILS_AND_AGREE_WITH_TERMS,
          undefined,
          'warning',
        );
      }
    } else {
      if (codeRetryCount < 3) {
        showToast(
          ToastMessages.auth.info.NO_ORGANISATION_IS_AVAILABLE_FOR_THIS_COMPANY_CODE,
          undefined,
          'warning',
        );
      } else {
        showToast(
          `Sorry, the email you entered (${formState.email}) cannot be used to sign up for ${companyCodeValue}.`,
          undefined,
          'warning',
        );

        dispatchForm({
          type: DispatchTypes.REGISTER_INPUT,
          values: {
            email: '',
            isAgree: false,
          },
        });
        setGetCompanyCode(false);
      }

      setCodeRetryCount((preState) => preState + 1);
    }
  };

  const sendLoginMagicRequest = async (
    email: string,
    isAgree: boolean,
    companyId: number,
    companyCode?: string,
  ) => {
    const type = isMobileApp ? LoginTypes.TOKEN : LoginTypes.LINK;

    if (!email || !isAgree || !type || !companyId) {
      setIsLoading(false);
      showToast(
        ToastMessages.auth.info.PLEASE_ENTER_VALID_DETAILS_AND_AGREE_WITH_TERMS,
        undefined,
        'warning',
      );

      return;
    }

    try {
      const response = await registerUser({
        email,
        isAgree,
        type,
        companyId,
      });

      if (response.ok) {
        handleSuccessfulRegistration(email);
      } else if (response.status === 400 || response.status === 403) {
        showToast(ToastMessages.auth.info.WE_COULD_NOT_RECOGNISE_THIS_EMAIL, undefined, 'warning');
      } else if (response?.status === 409) {
        showToast(
          ToastMessages.auth.info.EMAIL_ADDRESS_IS_ALREADY_REGISTERED,
          undefined,
          'warning',
        );
      } else {
        handleRegistrationError(response, email, companyCode);
      }
    } catch (error) {
      setIsLoading(false);
      showToast(ToastMessages.auth.info.ADD_USER_UNSUCCESSFUL_CONTACT_ADMIN, undefined, 'warning');
    }
  };

  const handleSuccessfulRegistration = (email: string) => {
    setIsLoading(false);

    history.push(
      buildCheckYourInboxPath(email, {
        companyURL,
        isMobileApp,
      }),
      {
        from: 'signUp',
      },
    );

    resetFormState();
  };

  const handleRegistrationError = (response: any, email: string, companyCode?: string) => {
    setIsLoading(false);

    switch (response.status) {
      case 409:
        resetFormState();
        showToast(
          ToastMessages.auth.info.EMAIL_ADDRESS_IS_ALREADY_REGISTERED,
          undefined,
          'warning',
        );
        setGetCompanyCode(false);
        break;

      case 403:
        handleForbiddenError(email, companyCode);
        break;

      default:
        showToast(
          ToastMessages.auth.info.ADD_USER_UNSUCCESSFUL_CONTACT_ADMIN,
          undefined,
          'warning',
        );
        break;
    }
  };

  const handleForbiddenError = (email: string, companyCode?: string) => {
    const companyDisplay = companyCode !== undefined ? companyCode : 'this company';

    showToast(
      `Sorry, the email you entered (${email}) cannot be used to sign up for ${companyDisplay}.`,
      undefined,
      'warning',
    );

    if (codeRetryCount > 3) {
      resetFormState();
      setGetCompanyCode(false);
    }

    setCodeRetryCount((prevState) => prevState + 1);
  };

  const resetFormState = () => {
    dispatchForm({
      type: DispatchTypes.REGISTER_INPUT,
      values: {
        email: '',
        isAgree: false,
      },
    });
  };

  const handleCompanyLoginMethods = async (
    companyUrl: string,
    userDetails: { email: string; isAgree: boolean; companyId: number },
    companyCode?: string,
  ) => {
    try {
      const companyLoginMethods = await getCompanyLoginMethodsByUrl(companyUrl);
      const companyLoginMethodsCast =
        castResponse<CompanyLoginMethodsInterface>(companyLoginMethods);

      if (!companyLoginMethods?.success || !companyLoginMethodsCast) {
        await sendLoginMagicRequest(
          userDetails.email,
          userDetails.isAgree,
          userDetails.companyId,
          companyCode,
        );

        return;
      }

      const loginMethods = companyLoginMethodsCast.companyLoginMethods;

      if (loginMethods?.length === 1) {
        const isSso = loginMethods[0]?.loginMethod === LoginMethods.SSO;

        if (isSso) {
          setIsLoading(true);

          try {
            const response = await registerUser({
              email: userDetails?.email,
              isAgree: userDetails?.isAgree,
              type: LoginTypes.SSO,
              companyId: userDetails?.companyId,
            });

            if (response.ok) {
              showToast(ToastMessages.auth.info.REDIRECTING_TO_SINGLE_SIGN_ON, undefined, 'info');
              window.location.replace(loginMethods[0]?.loginUrl);
            } else if (response.status === 400 || response.status === 403) {
              showToast(
                ToastMessages.auth.info.WE_COULD_NOT_RECOGNISE_THIS_EMAIL,
                undefined,
                'warning',
              );
            } else {
              showToast(
                ToastMessages.auth.error.LOGIN_UNSUCCESSFUL_CONTACT_ADMIN,
                undefined,
                'error',
              );
            }
          } catch (error) {
            showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'error');
          } finally {
            setIsLoading(false);
          }
        } else {
          await sendLoginMagicRequest(
            userDetails.email,
            userDetails.isAgree,
            userDetails.companyId,
            companyCode,
          );
        }
      } else {
        // Handle multiple login methods or no methods
        handleLoginMethodsToDisplay(loginMethods || [], userDetails);

        if (!loginMethods?.length) {
          await sendLoginMagicRequest(
            userDetails.email,
            userDetails.isAgree,
            userDetails.companyId,
            companyCode,
          );
        }
      }
    } catch (error) {
      await sendLoginMagicRequest(
        userDetails.email,
        userDetails.isAgree,
        userDetails.companyId,
        companyCode,
      );
    }
  };

  const createUser = async (
    email: string,
    isAgree: boolean,
    companyId: number,
    companyCode?: string,
  ) => {
    setIsLoading(true);

    if (!email) {
      setIsLoading(false);
      showToast(
        ToastMessages.auth.info.PLEASE_ENTER_VALID_DETAILS_AND_AGREE_WITH_TERMS,
        undefined,
        'warning',
      );

      return;
    }

    // Handle mobile app case
    if (isMobileApp) {
      await sendLoginMagicRequest(email, isAgree, companyId, companyCode);

      return;
    }

    // Handle web app cases
    const userDetails = { email, isAgree, companyId };
    const targetCompanyUrl = companyURL || companyDomainDetails?.company_url;

    if (targetCompanyUrl) {
      await handleCompanyLoginMethods(targetCompanyUrl, userDetails, companyCode);
    } else {
      await sendLoginMagicRequest(email, isAgree, companyId, companyCode);
    }

    setIsLoading(false);
  };

  return (
    <>
      {isLoading && <LoadingBackdrop text="Connecting to your organisation..." />}
      {getCompanyCode
        ? 'Enter your unique code that is provided to you by your organisation so we can connect you. If you can’t find your unique organisation code, check with your organisation or let us know the name of your organisation by emailing hello@hapstar.app so we can help.'
        : content}
      {getCompanyCode ? (
        <>
          <GetCompanyCodeForm onClick={handleValidateCompanyCodeCreateUser} />
          <div className={styles.linkHolder}>
            <p>
              <LinkText onClick={() => setGetCompanyCode(false)}>Back</LinkText>
            </p>
          </div>
        </>
      ) : (
        <>
          <RegistrationForm onClick={handleCreateUser} isUserCreating={isLoading} />
          <div className={styles.linkHolder}>
            <p>
              Already got an account?{' '}
              <LinkText
                path={`/login${companyURL ? `/${companyURL}` : ''}${
                  isMobileApp ? '?isMobileApp=true' : ''
                }`}
              >
                Login here
              </LinkText>
            </p>
          </div>
        </>
      )}
    </>
  );
};

SignUp.displayName = 'SignUp';

SignUp.defaultProps = {
  organizationId: undefined,
  companyURL: undefined,
};

interface SignUpProps
  extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  content: JSX.Element | string | undefined;
  organizationId?: number;
  companyURL?: string;
  handleLoginMethodsToDisplay: (
    methods: CompanyLoginMethodTypesInterface[],
    userDetails: {
      email: string;
      isAgree: boolean;
      companyId: number;
    },
  ) => void;
}

export default SignUp;
